import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Handshake, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFuzzyRate } from '../hooks/useFuzzyRate';

const tg = () => window.Telegram?.WebApp;
const haptic = (style = 'light') => { try { tg()?.HapticFeedback?.impactOccurred(style); } catch (e) {} };

/**
 * MakeOfferDialog
 * - Triggered from GameRoom (hosted Prize Rooms with availableSlots === 10).
 * - Posts to /api/offers; backend validates min % and reserves balance.
 */
export default function MakeOfferDialog({ open, onOpenChange, game, userBalance, onSuccess }) {
  const { user } = useAuth();
  const roomCur = game?.currency === 'FUZZY' ? 'FUZZY' : 'XRP';
  const [offerCur, setOfferCur] = useState(roomCur);
  const fuzzyRate = useFuzzyRate(!!game?.bin_flex_currency || roomCur === 'FUZZY');
  const roomBin = +(game?.total_cost * 10).toFixed(2);
  const flex = !!game?.bin_flex_currency;
  const payCur = flex ? offerCur : roomCur;
  const binPrice = payCur === roomCur ? roomBin
    : (fuzzyRate ? +(roomCur === 'XRP' ? roomBin * fuzzyRate.fuzzy_per_xrp : roomBin * fuzzyRate.xrp_per_fuzzy).toFixed(2) : null);
  const minOfferPct = 0.5;
  const minAllowed = binPrice != null ? +(binPrice * minOfferPct).toFixed(2) : null;
  const payBalance = payCur === 'FUZZY' ? (user?.fuzzy_balance || 0) : (user?.balance ?? userBalance ?? 0);

  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open || !game) return;
    setAmount('');
    setMessage('');
    setExisting(null);
    axios.get(`${API}/offers/listing/${game.game_id}`)
      .then(r => setExisting(r.data?.offer || null))
      .catch(() => {});
  }, [open, game]);

  const numAmount = parseFloat(amount);
  const isValid =
    binPrice != null &&
    !isNaN(numAmount) &&
    numAmount >= minAllowed &&
    numAmount < binPrice &&
    numAmount <= payBalance;

  const submit = async () => {
    if (!isValid) return;
    haptic('medium');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/offers`, {
        game_id: game.game_id,
        offer_amount: numAmount,
        currency: payCur,
        message: message.trim(),
      });
      toast.success(`Offer sent: ${numAmount.toLocaleString()} ${payCur}`);
      onSuccess?.(res.data?.offer);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit offer');
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async () => {
    if (!existing) return;
    setLoading(true);
    try {
      await axios.post(`${API}/offers/${existing.offer_id}/withdraw`);
      toast.success('Offer withdrawn');
      setExisting(null);
      onSuccess?.(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to withdraw');
    } finally {
      setLoading(false);
    }
  };

  if (existing && open) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="existing-offer-dialog" className="bg-slate-900 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Handshake className="w-5 h-5 text-amber-400" /> Offer Pending
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              You already have an offer on this listing.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 my-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Your offer</p>
            <p className="text-2xl font-bold text-amber-300">{existing.offer_amount?.toLocaleString?.() ?? existing.offer_amount} {existing.currency || 'XRP'}</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Status: <span className="text-amber-300 font-semibold">{existing.status}</span>
            </p>
            {existing.status === 'countered' && (
              <p className="text-[11px] text-orange-300 mt-1">
                Host countered at <strong>{existing.counter_amount} {existing.currency || 'XRP'}</strong> — review in My Games.
              </p>
            )}
          </div>
          <Button
            data-testid="withdraw-offer-button"
            variant="outline"
            disabled={loading}
            onClick={withdraw}
            className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Withdraw Offer'}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="make-offer-dialog" className="bg-slate-900 border-slate-700 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Handshake className="w-5 h-5 text-amber-400" /> Make an Offer
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Offer must be below BIN price. Host has 48h to respond.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          {flex && (
            <div className="grid grid-cols-2 gap-2">
              {['XRP', 'FUZZY'].map(c => (
                <button
                  key={c}
                  data-testid={`offer-currency-${c.toLowerCase()}`}
                  onClick={() => setOfferCur(c)}
                  className={`rounded-xl border p-2 text-center text-xs font-bold transition-colors ${payCur === c ? 'bg-amber-500/15 border-amber-500/50 text-amber-300' : 'bg-slate-800/50 border-slate-700 text-white'}`}
                >
                  Offer in {c}
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-between text-[11px] text-slate-400">
            <span>BIN Price</span><span className="text-white font-bold">{binPrice == null ? '…' : `${binPrice.toLocaleString()} ${payCur}`}</span>
          </div>
          <div className="flex justify-between text-[11px] text-slate-400">
            <span>Min Offer ({Math.round(minOfferPct * 100)}%)</span><span className="text-white">{minAllowed == null ? '…' : `${minAllowed.toLocaleString()} ${payCur}`}</span>
          </div>
          <div className="flex justify-between text-[11px] text-slate-400">
            <span>Your Balance</span><span className="text-white">{payBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} {payCur}</span>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Your Offer ({payCur})</label>
            <Input
              data-testid="offer-amount-input"
              type="number"
              step="0.01"
              min={minAllowed}
              max={binPrice - 0.01}
              placeholder={`${minAllowed} – ${(binPrice - 0.01).toFixed(2)}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
            />
            {amount && !isValid && (
              <p className="text-[10px] text-red-400 mt-1">
                {numAmount > (userBalance || 0)
                  ? `Insufficient balance (need ${numAmount} ${payCur})`
                  : numAmount >= binPrice
                  ? `Must be below BIN (${binPrice})`
                  : numAmount < minAllowed
                  ? `Must be at least ${minAllowed} ${payCur}`
                  : 'Invalid amount'}
              </p>
            )}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Message (optional)</label>
            <Textarea
              data-testid="offer-message-input"
              placeholder="Hey, would you take..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={280}
              className="bg-slate-800 border-slate-700 text-white text-sm h-16"
            />
            <p className="text-[10px] text-slate-600 text-right mt-0.5">{message.length}/280</p>
          </div>
        </div>

        <Button
          data-testid="submit-offer-button"
          disabled={!isValid || loading}
          onClick={submit}
          className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send Offer · ${amount || '–'} ${payCur}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
